import React, { useState, useEffect, useRef, useMemo } from 'react';
import Header from '../home/components/Header.tsx';
import {
  clearRedPointStatusApi,
  getCopilotHistoryInfoApi,
  getCopilotValueByTaskIdApi,
  getRedPointStatusApi,
  qryCopilotValueApi
} from '@/api/copilot.ts';
import TabNavigation from '@/components/TabNavigation.tsx';
import ProductRecommend from './components/tabs/ProductRecommendTab.tsx';
import ProductComparisonTab from './components/tabs/ProductComparisonTab.tsx';
import ProductInterpretationTab from './components/tabs/ProductInterpretationTab.tsx';
import MarketInterpretationTab from './components/tabs/MarketInterpretationTab.tsx';
import CustomerNeedsRecognition from './components/CustomerNeedsRecognition';
import { useNavigate } from 'react-router-dom';
import { History } from 'lucide-react';
import customerAvatar from '@/imgs/customer-avatar.png';
import { toast } from 'sonner';
import { usePollingApi } from '@/hooks/usePollingApi';
import HistoryInsightsDialog from './components/HistoryInsightsDialog';
import { useAuth } from '@/contexts/AuthContext';
import { type HistoryInsight } from '@/api/types';
import useUserStore from '@/stores/userStore';
import {
  type CopilotValue8,
  type CopilotValue9,
  type CopilotValue10,
  type CopilotValue11
} from '@/stores/types/copilotValue';
import {
  defaultCopilotValue8,
  defaultCopilotValue9,
  defaultCopilotValue10,
  defaultCopilotValue11
} from '@/stores/copilotValueStore';
import useCopilotValueStore from '@/stores/copilotValueStore';
import { SkeletonDashboard } from '@/components/loading/Skeleton';
import { EXTERNAL_USER_ID } from '@/config/env.ts';
// 类型定义

const mapTabValueToTab: Record<string, string> = {
  scjd: 'marketInterpretation',
  cpdb: 'productComparison',
  cpjd: 'productInterpretation',
  cptj: 'productRecommend',
  marketInterpretation: 'scjd',
  productComparison: 'cpdb',
  productInterpretation: 'cpjd',
  productRecommend: 'cptj'
};

// 修复：用 forwardRef 包裹，明确 props 和 ref 类型（避免 any）
const DiagnosisPage: React.FC = () => {
  const { externalUserId, followUserId } = useAuth();
  const { taskId: currentTaskId, updateTaskId } = useUserStore();
  const {
    copilotValue8,
    copilotValue9,
    copilotValue10,
    copilotValue11,
    updateCopilotValue8,
    updateCopilotValue9,
    updateCopilotValue10,
    updateCopilotValue11
  } = useCopilotValueStore();
  let initTaskId = currentTaskId;
  let initActiveTab = null;
  const navigate = useNavigate();
  const hasFetched = useRef(false);
  const [initLoading, setInitLoading] = useState(true);
  const [userQuestion, setUserQuestion] = useState<string>('正在获取用户问题，请稍后...');

  const [historyContent, setHistoryContent] = useState<string>('');
  const [historyKeyWord, setHistoryKeyWord] = useState<string>('');

  // 产品推荐 agent
  const { startPolling: startPolling8, stopPolling: stopPolling8 } = usePollingApi(
    // API 调用函数
    () =>
      getCopilotValueByTaskIdApi({
        externalUserId,
        reqType: 8,
        taskId: initTaskId,
        followUserId
      }),
    // 检查条件：status === 'false' 时继续轮询，'true' 时停止
    response => {
      setInitLoading(false);
      console.log('轮询响应:', response);
      return response?.status === 'false';
    },
    {
      interval: 1000,
      onSuccess: (data: CopilotValue8) => {
        updateCopilotValue8(data);
        console.log('轮询成功，获取到数据:', data);
        updateCopilotValue8({ loading: false });
        setUserQuestion(data.content || userQuestion || '正在获取用户问题，请稍后...');
        setExtractedNeeds(data.keyWord || '正在获取关键词，请稍后...');
        // setTotalData8(data);
      },
      onError: error => {
        console.error('轮询失败:', error);
        updateCopilotValue8({ ...defaultCopilotValue8, loading: false });
      }
    }
  );

  // 产品对比 agent
  const { startPolling: startPolling9, stopPolling: stopPolling9 } = usePollingApi(
    // API 调用函数
    () =>
      getCopilotValueByTaskIdApi({
        externalUserId,
        reqType: 9,
        taskId: initTaskId,
        followUserId
      }),
    // 检查条件：status === 'false' 时继续轮询，'true' 时停止
    response => {
      setInitLoading(false);
      console.log('轮询响应:', response);
      return response?.status === 'false';
    },
    {
      interval: 1000,
      onSuccess: (data: CopilotValue9) => {
        updateCopilotValue9(data);
        console.log('轮询成功，获取到数据:', data);
        updateCopilotValue9({ loading: false });
        setUserQuestion(data.content || userQuestion || '正在获取用户问题，请稍后...');
        setExtractedNeeds(data.keyWord || '正在获取关键词，请稍后...');
      },
      onError: error => {
        console.error('轮询失败:', error);
        toast.error(error || '数据获取失败');
        updateCopilotValue9({ loading: false });
        updateCopilotValue9({ ...defaultCopilotValue9, loading: false });
      }
    }
  );

  // 产品解读 agent
  const { startPolling: startPolling10, stopPolling: stopPolling10 } = usePollingApi(
    // API 调用函数
    () =>
      getCopilotValueByTaskIdApi({
        externalUserId,
        reqType: 10,
        taskId: initTaskId,
        followUserId
      }),
    // 检查条件：status === 'false' 时继续轮询，'true' 时停止
    response => {
      setInitLoading(false);
      console.log('轮询响应:', response);
      updateCopilotValue10({ loading: response?.status === 'false' });
      return response?.status === 'false';
    },
    {
      interval: 1000,
      onSuccess: (data: CopilotValue10) => {
        updateCopilotValue10(data);
        console.log('轮询成功，获取到数据:', data);
        updateCopilotValue10({ loading: false });
        setUserQuestion(data.content || userQuestion || '正在获取用户问题，请稍后...');
        setExtractedNeeds(data.keyWord || '正在获取关键词，请稍后...');
      },
      onError: error => {
        console.error('轮询失败:', error);
        toast.error(error || '数据获取失败');
        updateCopilotValue10({ loading: false });
        updateCopilotValue10({ ...defaultCopilotValue10, loading: false });
      }
    }
  );

  // 市场解读 agent
  const { startPolling: startPolling11, stopPolling: stopPolling11 } = usePollingApi(
    // API 调用函数
    () =>
      getCopilotValueByTaskIdApi({
        externalUserId,
        reqType: 11,
        taskId: initTaskId,
        followUserId
      }),
    // 检查条件：status === 'false' 时继续轮询，'true' 时停止
    response => {
      setInitLoading(false);
      console.log('轮询响应:', response);
      updateCopilotValue11({ loading: response?.status === 'false' });
      return response?.status === 'false';
    },
    {
      interval: 1000,
      onSuccess: (data: CopilotValue11) => {
        console.log('轮询成功，获取到数据:', data);
        updateCopilotValue11(data);
        updateCopilotValue11({ loading: false });
        setUserQuestion(data.content || userQuestion || '正在获取用户问题，请稍后...');
        setExtractedNeeds(data.keyWord || '正在获取关键词，请稍后...');
        // toast.success('数据获取成功');
      },
      onError: error => {
        console.error('轮询失败:', error);
        updateCopilotValue11({ loading: false });
        updateCopilotValue11({ ...defaultCopilotValue11, loading: false });
      }
    }
  );

  const [historyDialogOpen, setHistoryDialogOpen] = useState(false);
  const [activeTab, setActiveTab] = useState<string>('productRecommend');
  initActiveTab = activeTab;

  const acitveCopilotValue = useMemo(() => {
    if (activeTab === 'productRecommend') {
      return copilotValue8;
    } else if (activeTab === 'productComparison') {
      return copilotValue9;
    } else if (activeTab === 'productInterpretation') {
      return copilotValue10;
    } else if (activeTab === 'marketInterpretation') {
      return copilotValue11;
    }
  }, [activeTab, copilotValue8, copilotValue9, copilotValue10, copilotValue11]);

  const activeLoading = useMemo(() => {
    return acitveCopilotValue?.loading;
  }, [acitveCopilotValue]);

  const loading = useMemo(() => {
    return initLoading && activeLoading;
  }, [initLoading, activeLoading]);

  const currentComponentRef = useRef<any>(null);

  const stopPollingAll = () => {
    stopPolling8();
    stopPolling9();
    stopPolling10();
    stopPolling11();
  };

  const startPollingByTabs = () => {
    console.log('initActiveTab', initActiveTab);
    if (initActiveTab === 'productRecommend') {
      updateCopilotValue8({ loading: true });
      stopPollingAll();
      startPolling8();
    } else if (initActiveTab === 'productComparison') {
      updateCopilotValue9({ loading: true });
      stopPollingAll();
      startPolling9();
    } else if (initActiveTab === 'productInterpretation') {
      updateCopilotValue10({ loading: true });
      stopPollingAll();
      startPolling10();
    } else if (initActiveTab === 'marketInterpretation') {
      updateCopilotValue11({ loading: true });
      stopPollingAll();
      startPolling11();
    }
  };

  // 获取初始任务ID并开始轮询
  const fetchCopilotData = async () => {
    try {
      if (!initTaskId) {
        qryCopilotValueApi({
          externalUserId,
          reqType: 0,
          followUserId
        })
          .then(response => {
            updateTaskId(response as string);
            initTaskId = response as string;
            startPollingByTabs();
          })
          .catch(error => {
            console.log(error);
          });
      } else {
        startPollingByTabs();
      }
    } catch (error) {
      console.error('获取Copilot数据失败:', error);
      toast.error('获取任务ID失败');
    }
  };

  // 初始化数据获取 - 必须在 useEffect 中执行，避免在渲染时更新其他组件
  useEffect(() => {
    if (!hasFetched.current) {
      hasFetched.current = true;
      fetchCopilotData();
      getCopilotHistoryInfo();
    }
  }, []); // 空依赖数组，只在组件挂载时执行一次

  // 组件卸载时停止轮询
  useEffect(() => {
    return () => {
      stopPollingAll();
    };
  }, []);

  const tabs = [
    { label: '产品推荐', value: 'productRecommend' },
    { label: '产品对比', value: 'productComparison' },
    { label: '产品解读', value: 'productInterpretation' },
    { label: '市场解读', value: 'marketInterpretation' }
  ];

  const goBack = () => {
    navigate(-1);
  };

  const handleRefresh = () => {
    console.log(activeLoading, initLoading, 'dldldld');
    if (activeLoading || initLoading) {
      toast.error('正在加载中，请稍后再试');
      return;
    }
    setInitLoading(true);
    startPollingByTabs();
  };

  const [extractedNeeds, setExtractedNeeds] = useState<string>('正在获取关键词，请稍后...');

  // 使用 ref 标记是否正在应用历史洞察，避免 useEffect 覆盖手动设置的值
  const isApplyingHistoryRef = useRef(false);

  useEffect(() => {
    // 如果正在应用历史洞察，跳过自动更新
    if (isApplyingHistoryRef.current) {
      isApplyingHistoryRef.current = false;
      return;
    }
    setUserQuestion(acitveCopilotValue?.content || historyContent || '');
    setExtractedNeeds(acitveCopilotValue?.keyWord || historyKeyWord || '');
    console.log('acitveCopilotValue', acitveCopilotValue);
  }, [acitveCopilotValue]);

  const handleUpdateActiveTab = (tab: string) => {
    initActiveTab = tab;
    setActiveTab(tab);
    fetchCopilotData();
    // if (tab === 'productRecommend') {
    //   setUserQuestion(copilotValue8?.content || historyContent || '正在获取用户问题，请稍后...');
    //   setExtractedNeeds(copilotValue8?.keyWord || historyKeyWord || '正在获取关键词，请稍后...');
    // } else if (tab === 'productComparison') {
    //   setUserQuestion(copilotValue9?.content || historyContent || '正在获取用户问题，请稍后...');
    //   setExtractedNeeds(copilotValue9?.keyWord || historyKeyWord || '正在获取关键词，请稍后...');
    // } else if (tab === 'productInterpretation') {
    //   setUserQuestion(copilotValue10?.content || historyContent || '正在获取用户问题，请稍后...');
    //   setExtractedNeeds(copilotValue10?.keyWord || historyKeyWord || '正在获取关键词，请稍后...');
    // } else if (tab === 'marketInterpretation') {
    //   setUserQuestion(copilotValue11?.content || historyContent || '正在获取用户问题，请稍后...');
    //   setExtractedNeeds(copilotValue11?.keyWord || historyKeyWord || '正在获取关键词，请稍后...');
    // }
  };

  const getCopilotHistoryInfo = () => {
    const tabType = mapTabValueToTab[activeTab];
    // 获取历史洞察
    getCopilotHistoryInfoApi({
      externalUserId: EXTERNAL_USER_ID,
      followUserId: followUserId,
      tabType
    })
      .then(res => {
        const resultArr = res || [];
        const tabHistory = resultArr?.[0];

        setHistoryContent(tabHistory?.content || '');
        setHistoryKeyWord(tabHistory?.keyWord || '');
      })
      .catch(error => {
        console.log(error, 'error');
      });
  };

  const handleUpdateActiveTabBefore = (tab: string) => {
    handleUpdateActiveTab(tab);
    getCopilotHistoryInfo();
  };

  const handleHistory = async () => {
    setHistoryDialogOpen(true);
  };

  const handleReapply = (insight: HistoryInsight) => {
    console.log('currentComponentRefcurrentComponentRef', insight, currentComponentRef.current);
    currentComponentRef.current?.resetSelectedValues();
    // 市场解读 scjd
    // 产品对比 cpdb
    // 产品解读 cpjd
    // 产品推荐 cptj
    const tabValue = insight.historyInfoList?.[0]?.tabValue;

    // 标记正在应用历史洞察，避免 useEffect 覆盖手动设置的值
    isApplyingHistoryRef.current = true;

    setUserQuestion(insight.content);
    setExtractedNeeds(insight.keyWord);
    setHistoryDialogOpen(false);
    if (tabValue === 'scjd') {
      setActiveTab('marketInterpretation');
      updateCopilotValue11({ loading: false });
      stopPolling11();
      try {
        updateCopilotValue11(JSON.parse(insight.jsonStr) as CopilotValue11);
      } catch (error) {
        toast.error(error || '数据解析失败');
        updateCopilotValue11({ ...defaultCopilotValue11, loading: false });
      }
    } else if (tabValue === 'cpdb') {
      updateCopilotValue9({ loading: false });
      stopPolling9();
      try {
        updateCopilotValue9(JSON.parse(insight.jsonStr) as CopilotValue9);
      } catch (error) {
        toast.error(error || '数据解析失败');
        updateCopilotValue9({ ...defaultCopilotValue9, loading: false });
      }
      setActiveTab('productComparison');
    } else if (tabValue === 'cpjd') {
      setActiveTab('productInterpretation');
      updateCopilotValue10({ loading: false });
      stopPolling10();
      try {
        updateCopilotValue10(JSON.parse(insight.jsonStr) as CopilotValue10);
      } catch (error) {
        toast.error(error || '数据解析失败');
        updateCopilotValue10({ ...defaultCopilotValue10, loading: false });
      }
    } else if (tabValue === 'cptj') {
      setActiveTab('productRecommend');
      updateCopilotValue8({ loading: false });
      stopPolling8();
      try {
        updateCopilotValue8(JSON.parse(insight.jsonStr) as CopilotValue8);
      } catch (error) {
        toast.error(error || '数据解析失败');
        updateCopilotValue8({ ...defaultCopilotValue8, loading: false });
      }
    } else {
      setActiveTab('productRecommend');
    }
  };

  const activeLabel = useMemo(() => {
    return tabs.find(tab => tab.value === activeTab)?.label;
  }, [activeTab]);

  const [redPointNum, setRedPointNum] = useState<{ [key: string]: number }>({});

  const { taskId } = useUserStore();

  // Tab 到 API value 的映射表
  const tabToValueMap: Record<string, number> = {
    productRecommend: 8,
    productComparison: 9,
    productInterpretation: 10,
    marketInterpretation: 11
  };

  // 获取所有 Tab 的红点状态
  const getRedPointStatus = async () => {
    if (!taskId) return;

    try {
      // 并行请求所有 Tab 的红点状态
      const promises = Object.entries(tabToValueMap).map(async ([tabKey, value]) => {
        const result = await getRedPointStatusApi({
          uuid: taskId,
          value
        });
        return { tabKey, count: Number(result || 0) };
      });

      const results = await Promise.all(promises);

      // 将结果转换为对象格式
      const redPointNumObj = results.reduce((acc, { tabKey, count }) => {
        acc[tabKey] = count;
        return acc;
      }, {} as Record<string, number>);

      setRedPointNum(redPointNumObj);
    } catch (error) {
      console.error('获取红点状态失败:', error);
      // 发生错误时设置为默认值
      setRedPointNum({
        productRecommend: 0,
        productComparison: 0,
        productInterpretation: 0,
        marketInterpretation: 0
      });
    }
  };

  useEffect(() => {
    if (taskId) {
      getRedPointStatus();
    }
  }, [taskId]);

  // 清除并更新红点状态
  useEffect(() => {
    if (!activeTab || !taskId) return;

    const value = tabToValueMap[activeTab];
    if (!value) return;

    const updateRedPointStatus = async () => {
      try {
        await clearRedPointStatusApi({
          uuid: taskId,
          value
        });

        const res = await getRedPointStatusApi({
          uuid: taskId,
          value
        });

        setRedPointNum(prev => ({
          ...prev,
          [activeTab]: Number(res || 0)
        }));
      } catch (error) {
        console.error(`更新 ${activeTab} 红点状态失败:`, error);
      }
    };

    updateRedPointStatus();
  }, [activeTab, taskId]);

  return (
    <div className="relative mx-auto flex h-screen flex-col overflow-hidden">
      <Header goBack={goBack} refresh={handleRefresh}>
        <div className="text-lg font-semibold leading-none tracking-tight">AI投顾洞察</div>
      </Header>

      <div className="relative flex h-[calc(100vh-66px)] flex-col px-4">
        {loading && (
          <div className="absolute left-0 top-0 z-[1111111] flex h-full w-full items-center justify-center">
            <SkeletonDashboard className="h-full w-full bg-white " />
          </div>
        )}
        <div className="mb-3 flex items-center justify-between gap-1">
          {/* 头像容器 */}
          <div className="flex">
            <div className="mr-1 h-6 w-6 overflow-hidden">
              <img src={customerAvatar} alt="客户头像" className="h-full w-full object-cover" />
            </div>
            {/* 标题 */}
            <span className="text-base font-semibold leading-6 text-[#1F1F1F]">客户需求识别</span>
          </div>
          <div>
            <button
              onClick={handleHistory}
              className="flex items-center rounded bg-gradient-to-r from-[#5D2BC7] to-[#A644CA] px-2 py-1 text-sm text-white shadow-sm transition-opacity hover:opacity-90"
            >
              <History className="mr-2" size={16} />
              历史洞察
            </button>
          </div>
        </div>
        <div className="flex flex-grow-[1] flex-col overflow-y-scroll">
          {/* 客户需求识别组件 */}
          <CustomerNeedsRecognition
            userQuestion={userQuestion || historyContent}
            extractedNeeds={extractedNeeds || historyKeyWord}
            activeLabel={activeLabel}
          />

          <div className="flex flex-grow-[1] flex-col rounded-lg bg-white p-3">
            <TabNavigation
              tabs={tabs}
              activeTab={activeTab}
              onUpdateActiveTab={handleUpdateActiveTabBefore}
              clsWrapper="px-0"
              redPointNum={redPointNum}
            />
            <div className="relative space-y-3 overflow-hidden pb-4">
              {(() => {
                const tabComponents = {
                  productRecommend: ProductRecommend,
                  productComparison: ProductComparisonTab,
                  productInterpretation: ProductInterpretationTab
                };

                const CurrentComponent = tabComponents[activeTab];

                // const componentProps = {
                //   productRecommend: { loading: loading8 },
                //   productComparison: { loading: loading9 },
                //   productInterpretation: { loading: loading10 },
                //   marketInterpretation: {
                //     loading: loading11,
                //     userQuestion: userQuestion
                //   }
                // };

                const refEnabledTabs = ['productComparison', 'productInterpretation'];

                if (activeTab === 'marketInterpretation') {
                  return (
                    <MarketInterpretationTab
                      ref={currentComponentRef}
                      userQuestion={userQuestion}
                    />
                  );
                }

                // 组合参数并渲染组件
                if (CurrentComponent) {
                  return refEnabledTabs.includes(activeTab) ? (
                    <CurrentComponent ref={currentComponentRef} />
                  ) : (
                    <CurrentComponent />
                  );
                }
                return null;
              })()}
            </div>
          </div>
        </div>
      </div>

      {/* 历史洞察 Dialog */}
      <HistoryInsightsDialog
        open={historyDialogOpen}
        onOpenChange={setHistoryDialogOpen}
        followUserId={followUserId}
        handleReapply={handleReapply}
      />
    </div>
  );
};

DiagnosisPage.displayName = 'DiagnosisPage';
export default DiagnosisPage;
